import _ from 'lodash';
// eslint-disable-next-line import/extensions
import { APPLY_RATES_TO, SHIPMENT_STATUS, STOP_STATUS, STATUS_SHIPMENT_CONSTANTS } from '@/utils/constants';

export const RatesEngineMixin = {
    methods: {
        $_getTotalAmount(ratesList) {
            let total = 0;
            ratesList.forEach((line) => {
                total += Math.round((line.quantity * line.unitPriceExTax + Number.EPSILON) * 100) / 100;
            });
            return total;
        },
        $_getRateAmount(rate) {
            const amount = rate.quantity * rate.unitPriceExTax;
            return Math.round((amount + Number.EPSILON) * 100) / 100;
        },
        $_getshipmentDataModel(
            shipmentDetails,
            user,
            loadValues = null,
            chargeList = null,
            costList = null,
            isCharging = false,
            isCosting = false,
            revertCharges = false,
            revertCosts = false,
            rateGroupChange = false,
            addressChange = false
        ) {
            const startTime = this.earliestStartTime(shipmentDetails?.shipmentStatusHistory);
            const finishTime = this.latestFinishTime(shipmentDetails?.shipmentStatusHistory);

            const pickup = {
                name: shipmentDetails.pickupStop.name,
                address: shipmentDetails.pickupStop.address,
                location: {
                    latitude: shipmentDetails.pickupStop.location.latitude,
                    longitude: shipmentDetails.pickupStop.location.longitude
                },
                tripDate: shipmentDetails.pickupStop.tripDate,
                earliestStartTime: startTime ?? null
            };

            const drop = {
                name: shipmentDetails.dropStop.name,
                address: shipmentDetails.dropStop.address,
                location: {
                    latitude: shipmentDetails.dropStop.location.latitude,
                    longitude: shipmentDetails.dropStop.location.longitude
                },
                latestFinishTime: finishTime ?? null
            };

            let pickupLocation = null;
            let dropLocation = null;
            let hasCustomerInvoice = false;
            let hasTeamMemberInvoice = false;
            let isChargingRule = isCharging;
            let isCostingRule = isCosting;

            if (
                shipmentDetails.pickupStop.location === null ||
                shipmentDetails.pickupStop.location?.latitude === 0 ||
                shipmentDetails.pickupStop.location?.longitude === 0
            ) {
                const status = ['Picked Up'];
                pickupLocation = this.getLocation(shipmentDetails?.shipmentStatusHistory, status);
            }

            if (
                shipmentDetails.dropStop.location === null ||
                shipmentDetails.dropStop.location?.latitude === 0 ||
                shipmentDetails.dropStop.location?.longitude === 0
            ) {
                const status = ['Complete'];
                dropLocation = this.getLocation(shipmentDetails?.shipmentStatusHistory, status);
            }

            if (pickupLocation) {
                pickup.location = pickupLocation;
            }

            if (dropLocation) {
                drop.location = dropLocation;
            }

            if (this.shipmentDetails.rateGroupId == null) {
                if (this.shipmentDetails.customerRateGroupId) {
                    // eslint-disable-next-line no-bitwise
                    const shouldCharge =
                        this.shipmentDetails.customerApplyRatesTo & this.getKeyByLabelInApplyRatesTo('Shipment');

                    if (shouldCharge && isCharging) {
                        isChargingRule = true;
                    } else {
                        isChargingRule = false;
                    }
                }

                if (this.shipmentDetails.assignedTo?.rateGroupId) {
                    // eslint-disable-next-line no-bitwise
                    const shouldCost =
                        this.shipmentDetails.assignedTo.applyRatesTo & this.getKeyByLabelInApplyRatesTo('Shipment');

                    if (shouldCost && isCosting) {
                        isCostingRule = true;
                    } else {
                        isCostingRule = false;
                    }
                }
            }

            if (shipmentDetails.customerInvoiceId !== null && shipmentDetails.customerInvoiceId !== undefined) {
                isChargingRule = false;
                hasCustomerInvoice = true;
            }

            if (shipmentDetails.teamMemberInvoiceId !== null && shipmentDetails.teamMemberInvoiceId !== undefined) {
                isCostingRule = false;
                hasTeamMemberInvoice = true;
            }

            // need to check if costs are a commission rate, and if so we need to update them based on the Base Charge.
            const isCommissionBasedCosts = this.costLinesAreCommissionBased(
                this.shipmentDetails.costLines,
                this.shipmentDetails.teamMemberInvoiceId
            );

            const shipmentWaitingTime = this.getTotalShipmentWaitingTime(shipmentDetails.shipmentStatusHistory);
            const jobEvents = this.getJobEventTimes(shipmentDetails.shipmentStatusHistory);

            const data = {
                pickup: JSON.stringify(pickup),
                drop: JSON.stringify(drop),
                tripDate: shipmentDetails.pickupStop.tripDate,
                rateGroupId: shipmentDetails.rateGroupId,
                load: loadValues,
                teamId: user.teamId,
                publicUserId: user.publicUserId,
                speedUnits: user.speedUnits,
                chargeList: JSON.stringify(chargeList),
                costList: JSON.stringify(costList),
                revertToDefaultCharges: revertCharges,
                revertToDefaultCosts: revertCosts,
                shipmentId: shipmentDetails.shipmentId,
                isChargingRule,
                isCostingRule,
                getDistance: true,
                customerId: shipmentDetails.customerId === null ? null : shipmentDetails.customerId,
                addressChanged: addressChange,
                rateGroupChanged: rateGroupChange,
                assignedTeamMemberPublicId: shipmentDetails.assignToPublicUserId
                    ? shipmentDetails.assignToPublicUserId
                    : shipmentDetails.assignedTo?.publicUserId,
                distance: shipmentDetails.distance ?? 0,
                costsAreCommission: isCommissionBasedCosts,
                hasCustomerInvoice,
                hasTeamMemberInvoice,
                customerRateGroupId: shipmentDetails.customerRateGroupId,
                teamMemberRateGroupId: shipmentDetails.assignedTo?.rateGroupId,
                isChargesApproved: shipmentDetails.isChargesApproved,
                isCostsApproved: shipmentDetails.isCostsApproved,
                waitingTime: shipmentWaitingTime,
                jobEventTimes: jobEvents,
                teamRegionId: shipmentDetails.teamRegionId
            };

            return data;
        },
        $_getStopData(
            stopDetails,
            user,
            loadValues = null,
            chargeList = null,
            costList = null,
            isCharging = false,
            isCosting = false,
            revertCharges = false,
            revertCosts = false,
            rateGroupChange = false,
            addressChange = false
        ) {
            let stopLoc = null;
            let hasCustomerInvoice = false;
            let hasTeamMemberInvoice = false;
            let isChargingRule = isCharging;
            let isCostingRule = isCosting;

            if (
                this.stopDetails.location === null ||
                this.stopDetails.location?.latitude === 0 ||
                this.stopDetails.location?.longitude === 0
            ) {
                const status = ['Complete', 'Failed'];
                stopLoc = this.getLocation(this.stopDetails.stopStatusHistory, status);
            }

            const drop = {
                name: stopDetails.name,
                address: stopDetails.address,
                location: {
                    latitude: stopDetails.location.latitude,
                    longitude: stopDetails.location.longitude
                }
            };

            if (stopLoc) {
                drop.location = stopLoc;
            }

            const pickup = {
                location: {
                    latitude: stopDetails.trip?.startLocation?.latitude,
                    longitude: stopDetails.trip?.startLocation?.longitude
                }
            };

            // need to check if costs are a commission rate, and if so we need to update them based on the Base Charge.
            const isCommissionBasedCosts = this.costLinesAreCommissionBased(
                this.stopDetails.costLines,
                this.stopDetails.teamMemberInvoiceId
            );

            if (this.stopDetails.rateGroupId == null) {
                if (this.stopDetails.customerRateGroupId) {
                    // eslint-disable-next-line no-bitwise
                    const shouldCharge =
                        this.stopDetails.customerApplyRatesTo & this.getKeyByLabelInApplyRatesTo('Stop');

                    if (shouldCharge && isCharging) {
                        isChargingRule = true;
                    } else {
                        isChargingRule = false;
                    }
                }

                if (this.stopDetails.assignedTo?.rateGroupId) {
                    // eslint-disable-next-line no-bitwise
                    const shouldcost =
                        this.stopDetails.assignedTo.applyRatesTo & this.getKeyByLabelInApplyRatesTo('Stop');

                    if (shouldcost && isCosting) {
                        isCostingRule = true;
                    } else {
                        isCostingRule = false;
                    }
                }
            }

            if (stopDetails.customerInvoiceId !== null && stopDetails.customerInvoiceId !== undefined) {
                isChargingRule = false;
                hasCustomerInvoice = true;
            }

            if (stopDetails.teamMemberInvoiceId !== null && stopDetails.teamMemberInvoiceId !== undefined) {
                isCostingRule = false;
                hasTeamMemberInvoice = true;
            }

            const stopWaitingTime = this.getTotalStopWaitingTimes(stopDetails.stopStatusHistory);

            const data = {
                pickup: JSON.stringify(pickup),
                drop: JSON.stringify(drop),
                tripDate: stopDetails.tripDate,
                rateGroupId: stopDetails.rateGroupId,
                load: loadValues,
                teamId: user.teamId,
                publicUserId: user.publicUserId,
                speedUnits: user.speedUnits,
                chargeList: JSON.stringify(chargeList),
                costList: JSON.stringify(costList),
                revertToDefaultCharges: revertCharges,
                revertToDefaultCosts: revertCosts,
                stopId: stopDetails.stopId,
                isChargingRule,
                isCostingRule,
                customerId: stopDetails.customerId === null ? null : stopDetails.customerId,
                assignedTeamMemberPublicId: stopDetails.assignToPublicUserId
                    ? stopDetails.assignToPublicUserId
                    : stopDetails.assignedTo?.publicUserId,
                rateGroupChanged: rateGroupChange ?? false,
                addressChanged: addressChange ?? false,
                stopAttemptCounter: stopDetails.stopAttemptCounter,
                stopStatus: stopDetails.status,
                previousAttemptStopId: stopDetails.previousAttemptStopId,
                costsAreCommission: isCommissionBasedCosts,
                hasCustomerInvoice,
                hasTeamMemberInvoice,
                customerRateGroupId: stopDetails.customerRateGroupId,
                teamMemberRateGroupId: stopDetails.assignedTo?.rateGroupId,
                isChargesApproved: stopDetails.isChargesApproved,
                isCostsApproved: stopDetails.isCostsApproved,
                waitingTime: stopWaitingTime,
                teamRegionId: stopDetails.teamRegionId
            };

            return data;
        },
        $_getRateRuleData(
            chargeType,
            publicUserId,
            rateDetail,
            customerId,
            listLength,
            rateGroupId,
            driverRuleCreation,
            driverUserId,
            teamRegionId
        ) {
            const obj = {
                type: chargeType.type,
                publicUserId,
                rule: JSON.stringify(rateDetail),
                customerId,
                priority: listLength + 1,
                rateGroupId: rateGroupId === 0 ? null : rateGroupId,
                isCostingRule: driverRuleCreation === true,
                isChargingRule: driverRuleCreation !== true,
                driverUserId,
                teamRegionId
            };

            return obj;
        },
        earliestStartTime(shipmentStatusHistory) {
            if (!shipmentStatusHistory || shipmentStatusHistory?.length === 0) {
                return null;
            }

            const startStatus = ['Pending', 'Enroute to Pickup', 'Arrived at Pickup', 'Picked Up'];
            let earliestTimes = shipmentStatusHistory.filter((status) => startStatus.includes(status.newStatus));

            if (earliestTimes.length === 0) {
                return null;
            }

            if (earliestTimes.find((x) => x.newStatus === 'Picked Up')) {
                earliestTimes = earliestTimes.filter((x) => x.newStatus === 'Picked Up');
            }

            earliestTimes.sort((a, b) => a.actionDate - b.actionDate);
            let startTime = earliestTimes[0].actionDate;

            earliestTimes.forEach((status) => {
                if (status.actionDate >= startTime) {
                    startTime = status.actionDate;
                }
            });

            return startTime.toString();
        },
        latestFinishTime(shipmentStatusHistory) {
            if (!shipmentStatusHistory || shipmentStatusHistory?.length === 0) {
                return null;
            }

            const finishStatus = ['Pending', 'Enroute to Drop', 'Arrived at Drop', 'Complete'];
            let latestFinishTimes = shipmentStatusHistory.filter((status) => finishStatus.includes(status.newStatus));

            if (latestFinishTimes.length === 0) {
                return null;
            }

            if (latestFinishTimes.find((x) => x.newStatus === 'Complete')) {
                latestFinishTimes = latestFinishTimes.filter((x) => x.newStatus === 'Complete');
            }

            latestFinishTimes.sort((a, b) => a.actionDate - b.actionDate);
            let finishTime = latestFinishTimes[0].actionDate;

            latestFinishTimes.forEach((status) => {
                if (status.actionDate >= finishTime) {
                    finishTime = status.actionDate;
                }
            });

            return finishTime.toString();
        },
        getTotalStopWaitingTimes(stopStatusHistory) {
            try {
                if (!stopStatusHistory || stopStatusHistory.length === 0) {
                    return null;
                }

                const arrivedAt = stopStatusHistory
                    .filter((item) => item.newStatus === STOP_STATUS.Arrived)
                    .sort((a, b) => a.actionDate - b.actionDate)
                    .pop();

                const completedAt = stopStatusHistory
                    .filter((item) => item.newStatus === STOP_STATUS.Complete)
                    .sort((a, b) => a.actionDate - b.actionDate)
                    .pop();

                if (!arrivedAt || !completedAt || completedAt.actionDate <= arrivedAt.actionDate) {
                    return null;
                }

                const waitingTime = new Date(completedAt.actionDate) - new Date(arrivedAt.actionDate);
                const waitingTimeMinutes = Math.ceil(waitingTime / (1000 * 60)); // Convert milliseconds to minutes

                return `00:${String(waitingTimeMinutes).padStart(2, '0')}:00`;
            } catch {
                return null;
            }
        },
        getTotalShipmentWaitingTime(shipmentStatusHistory) {
            if (!shipmentStatusHistory || shipmentStatusHistory.length === 0) {
                return null;
            }
            const waitingTimePickup = this.shipmentStopPickupWaitingTime(shipmentStatusHistory);
            const waitingTimeDrop = this.shipmentDropWaitingTime(shipmentStatusHistory);

            const totalMinutes = waitingTimeDrop + waitingTimePickup;
            return `00:${String(totalMinutes).padStart(2, '0')}:00`;
        },
        shipmentStopPickupWaitingTime(shipmentStatusHistory) {
            try {
                const arrivedAt = shipmentStatusHistory
                    .filter((x) => x.newStatus === SHIPMENT_STATUS.ArrivedAtPickup)
                    .sort((a, b) => a.actionDate - b.actionDate)
                    .pop();

                const completeArriveUp = shipmentStatusHistory
                    .filter((x) => x.newStatus === SHIPMENT_STATUS.PickedUp)
                    .sort((a, b) => a.actionDate - b.actionDate)
                    .pop();

                if (!arrivedAt || !completeArriveUp || completeArriveUp.actionDate <= arrivedAt.actionDate) {
                    return 0;
                }

                const waitingTimeMs = new Date(completeArriveUp.actionDate) - new Date(arrivedAt.actionDate);
                return Math.ceil(waitingTimeMs / (1000 * 60)); // Convert milliseconds to minutes
            } catch {
                return 0;
            }
        },
        shipmentDropWaitingTime(shipmentStatusHistory) {
            try {
                const arrivedAt = shipmentStatusHistory
                    .filter((x) => x.newStatus === SHIPMENT_STATUS.ArrivedAtDrop)
                    .sort((a, b) => a.actionDate - b.actionDate)
                    .pop();

                const completeDrop = shipmentStatusHistory
                    .filter((x) => x.newStatus === SHIPMENT_STATUS.Complete)
                    .sort((a, b) => a.actionDate - b.actionDate)
                    .pop();

                if (!arrivedAt || !completeDrop || completeDrop.actionDate <= arrivedAt.actionDate) {
                    return 0;
                }

                const waitingTimeMs = new Date(completeDrop.actionDate) - new Date(arrivedAt.actionDate);
                return Math.ceil(waitingTimeMs / (1000 * 60)); // Convert milliseconds to minutes
            } catch {
                return 0;
            }
        },
        $_getTripDataModel(
            tripDetails,
            user,
            loadValues = null,
            chargeList = null,
            costList = null,
            isCharging = false,
            isCosting = false,
            revertCharges = false,
            revertCosts = false,
            rateGroupChange = false,
            addressChange = false
        ) {
            const startAddress = {
                location: {
                    latitude: tripDetails.locationDetails.startLocation.latitude,
                    longitude: tripDetails.locationDetails.startLocation.longitude
                },
                tripDate: tripDetails.tripDate,
                earliestStartTime: tripDetails.actualStartTime
            };

            const endAddress = {
                location: {
                    latitude: tripDetails.locationDetails.endLocation?.latitude,
                    longitude: tripDetails.locationDetails.endLocation?.longitude
                },
                latestFinishTime: tripDetails.actualEndTime
            };

            const data = {
                pickup: JSON.stringify(startAddress),
                drop: JSON.stringify(endAddress),
                tripDate: tripDetails.tripDate,
                rateGroupId: tripDetails.rateGroupId,
                load: loadValues,
                teamId: user.teamId,
                publicUserId: user.publicUserId,
                speedUnits: user.speedUnits,
                chargeList: JSON.stringify(chargeList),
                costList: JSON.stringify(costList),
                revertToDefaultCharges: revertCharges,
                revertToDefaultCosts: revertCosts,
                tripId: tripDetails.tripId,
                isChargingRule: isCharging,
                isCostingRule: isCosting,
                getDistance: false,
                customerId: tripDetails.customerId === null ? null : tripDetails.customerId,
                addressChanged: addressChange,
                rateGroupChanged: rateGroupChange,
                assignedTeamMemberPublicId: tripDetails.assignToPublicUserId
                    ? tripDetails.assignToPublicUserId
                    : tripDetails.assignedTo?.publicUserId,
                distance: tripDetails.travelDistanceMetres,
                tripStartTime: tripDetails.actualStartTime,
                startTime: tripDetails.startTime,
                userDefaultTripStartTime: tripDetails.userDefaultTripStartTime,
                tripEndTime: tripDetails.actualEndTime,
                endTime: tripDetails.endTime,
                userDefaultTripEndTime: tripDetails.userDefaultTripEndTime,
                tripEstimatedEndTime: tripDetails.locationDetails.endEta,
                userTimeZone: tripDetails.userTimeZone,
                isChargesApproved: tripDetails.isChargesApproved,
                isCostsApproved: tripDetails.isCostsApproved,
                teamRegionId: tripDetails.teamRegionId
            };

            return data;
        },
        costLinesAreCommissionBased(costs, teamMemberInvoiceId) {
            const baseCharge = costs.find((c) => {
                return c.isBaseCharge === true;
            });

            if (baseCharge == null) 
                return false;

            if (baseCharge.type !== 'commission') {
                return false;
            }

            if (teamMemberInvoiceId) {
                return false;
            }

            return true;
        },
        getLocation(historyEvents, status) {
            if (!historyEvents || historyEvents?.length === 0) 
                return null;

            const statuses = status;
            const latestFinishTimes = historyEvents.filter((status) => statuses.includes(status.newStatus));

            if (latestFinishTimes.length === 0) {
                return null;
            }

            latestFinishTimes.sort((a, b) => a.actionDate - b.actionDate);

            return latestFinishTimes.pop().location;
        },
        getKeyByLabelInApplyRatesTo(label) {
            const applyRatesToKey = APPLY_RATES_TO.filter(function compareLabels(type) {
                if (type.label.toLowerCase() === label.toLowerCase()) 
                    return type.key;
                return null;
            });
            if (applyRatesToKey.length > 0) 
                return applyRatesToKey[0].key;
            return 0;
        },
        getJobEventTimes(shipmentStatusHistory) {
            if (!shipmentStatusHistory || shipmentStatusHistory.length === 0) {
                return null;
            }

            const validStatuses = shipmentStatusHistory
                .filter((item) => STATUS_SHIPMENT_CONSTANTS.includes(item.newStatus))
                .sort((a, b) => new Date(a.actionDate) - new Date(b.actionDate))
                .map((item) => ({
                    status: item.newStatus,
                    actionDate: item.actionDate
                }));

            return validStatuses.length > 0 ? validStatuses : null;
        },
        $_getRateRuleText(data, currency, rateType) {
            const messageData = [];
            const type = rateType;
            let baseRuleHasData = true;

            if (['hourly', 'item', 'weight', 'distance', 'volume', 'drop'].includes(type)) {
                if (data.interval === null && data.flagFall === null && data.includedValue === null &&
                        data.ratePerInterval === null && data.minimumCharge === null) {
                    baseRuleHasData = false;
                }

                if (baseRuleHasData) {
                    const baseRateMessage = this.buildBaseRateMessage(data, currency, type);
                    if (baseRateMessage) {
                        messageData.push(baseRateMessage);
                    }
                }

                // Handle general rules
                const generalRuleMessage = this.buildGeneralRuleMessage(data.generalRule, currency);
                if (generalRuleMessage) {
                    messageData.push(generalRuleMessage);
                }
            }            

            // Handle conditions
            if (data.ruleConditions) {
                const conditionMessages = this.buildConditionMessages(data.ruleConditions, type);

                if (conditionMessages.length > 0)
                {
                    messageData.push('Conditions');
                    messageData.push(...conditionMessages);
                }
            }

            return messageData;
        },
        buildBaseRateMessage(data, currency, type) {
            const parts = [];

            if (data.flagFall !== null) {
                parts.push(` There is a Flag fall charge of ${currency}${data.flagFall}`);
            }

            if (data.minimumCharge !== null) {
                parts.push(` Minimum charge of ${currency}${data.minimumCharge}`);
            }

            if (data.ratePerInterval !== null) {
                if (data.interval !== null) {
                    parts.push(` Rate per interval is ${currency}${data.ratePerInterval} per ${data.interval} unit(s)`);
                } else if (type === 'hourly') {
                    parts.push(` Hourly rate is ${currency}${data.ratePerInterval}`);
                }else if (type === 'distance') {
                    parts.push(` Rate of ${currency}${data.ratePerInterval} per km/mile`);
                }
            }

            if (data.includedValue != null && type === 'hourly') {
                parts.push(` this also has included time of ${data.includedValue} minutes`);
            } else if(data.includedValue !== null) {
                parts.push(` this also has an included unit(s) of ${data.includedValue}`);
            }

            return parts.length > 0 ? `${parts.join(', ')}.` : null;
        },
        buildGeneralRuleMessage(generalRule, currency) {
            const parts = [];

            if (generalRule === null || generalRule === '{}')
                return null;

            if (generalRule.waitingTimeRatePerHour !== null) {
                parts.push(` Waiting time rate per hour is ${currency}${generalRule.waitingTimeRatePerHour}`);
            }

            if (generalRule.waitTimeRatePerInterval !== null && generalRule.waitTimeInterval !== null) {
                parts.push(` with ${currency}${generalRule.waitTimeRatePerInterval} per ${generalRule.waitTimeInterval} minute(s)`);
            }

            if (generalRule.includedWaitingTime !== null) {
                parts.push(` there is also included waiting time of ${generalRule.includedWaitingTime} minute(s)`);
            }

            return parts.length > 0 ? `${parts.join(', ')}.` : null;
        },
        buildConditionMessages(conditions, rateType) {
            const messages = [];
            // Map of condition type to display label
            const conditionTypes = {
                fuelLevy: 'Fuel Levy',
                waitTime: 'Wait Time',
                surcharge: 'Surcharge',
                commission: 'Commission',
                item: 'Item',
                distance: 'Distance'
            };

            Object.keys(conditionTypes).forEach(type => {
                if (conditions[type] && rateType === type) {
                    messages.push(`${conditionTypes[type]}: ${conditions[type]}`);
                }
            });

            return messages;
        },
        $_prepareChargeRateEdit(rate, original, edited, originalUnit, trimmedUnit, isManualCharge, multiFacetBaseCharges, chargeType) {
            const descriptionChanged = original.description !== edited.description;
            const amountChanged = original.amount !== edited.amount;
            let chargeUnitChanged = original.chargeUnit !== originalUnit;

            let useNewEndpoint = false;
            if (rate.type === 'hourly') {
                chargeUnitChanged = original.timeInput !== edited.timeInput;
            }

            if ((chargeUnitChanged && !descriptionChanged && !amountChanged) ||
                (chargeUnitChanged && descriptionChanged && !amountChanged) ||
                (chargeUnitChanged && !descriptionChanged && amountChanged) ||
                (chargeUnitChanged && descriptionChanged && amountChanged)) {
                useNewEndpoint = true;
                rate.unitUpdated = true;
            }

            if (original.chargeUnit === '' || original.chargeUnit === null)
                original.chargeUnit = 0;

            rate.description = edited.description;
            rate.unitPriceExTax = edited.amount;
            rate.chargeUnit = chargeUnitChanged ? original.chargeUnit : null;
            const onlyChargeUnitUpdated = chargeUnitChanged && (!amountChanged && !descriptionChanged);

            rate.manuallyChanged = rate.manuallyChanged ? rate.manuallyChanged : !onlyChargeUnitUpdated && !rate.manuallyChanged;
            /* eslint-disable no-nested-ternary */
            rate.chargeUnitDescription = chargeUnitChanged && rate.type !== 'hourly'
                ? `${original.chargeUnit} ${trimmedUnit}` : chargeUnitChanged && rate.type === 'hourly'
                    ? original.timeInput : edited.chargeUnit;
            /* eslint-enable no-nested-ternary */

            let payload = '';
            if (multiFacetBaseCharges && multiFacetBaseCharges.length >= 1 && useNewEndpoint) {

                const multiFacetRates = [];
                multiFacetBaseCharges.forEach((x) => {
                    const rate = _.cloneDeep(x);
                    let trimmedChargeUnit;
        
                    if (rate.type === 'hourly') {
                        trimmedChargeUnit = this.$_convertToMinutes(rate.chargeUnit);
                    } else {
                        trimmedChargeUnit = this.$_extractNumber(rate.chargeUnit);
                    }
                    
                    rate.chargeUnit = trimmedChargeUnit;
                    multiFacetRates.push(rate)
                });

                // eslint-disable-next-line no-nested-ternary
                const lineKey = chargeType === 'Cost' ? 'costLines' : 'chargeLines';

                multiFacetRates.push(rate);
                payload = {
                    method: 'post',
                    data: {
                        [lineKey]: multiFacetRates.map(rate => ({
                            id: rate.id,
                            description: rate.description,
                            quantity: rate.quantity,
                            unitPriceExTax: rate.unitPriceExTax,
                            manuallyChanged: rate.manuallyChanged,
                            isManualCharge,
                            chargeUnit: rate.chargeUnit,
                            chargeUnitUpdated: rate.unitUpdated,
                            chargeUnitDescription: rate.chargeUnitDescription
                        }))
                    }
                }
            }else {
                payload = {
                    method: 'post',
                    data: {
                        id: rate.id,
                        description: rate.description,
                        quantity: rate.quantity,
                        unitPriceExTax: rate.unitPriceExTax,
                        manuallyChanged: rate.manuallyChanged,
                        isManualCharge,
                        chargeUnit: rate.chargeUnit,
                        chargeUnitUpdated: rate.unitUpdated,
                        chargeUnitDescription: rate.chargeUnitDescription
                    }
                };
            }

            return { rate, payload, useNewEndpoint };
        },
        $_startEdit(index, rate, editBuffer, originalEditBuffer) {
            this.editingIndex = index;
            const rateType = 'hourly';
            // Initialize edit buffer with current values
            editBuffer.description = rate.description;
            editBuffer.amount = this.$_getRateAmount(rate);
            editBuffer.chargeUnit = rate.chargeUnit;

            let trimmedChargeUnit;
            let trimmedUnit;

            if (rate.type === rateType) {
                trimmedChargeUnit = this.$_convertToMinutes(rate.chargeUnit);
                editBuffer.timeInput = rate.chargeUnit;
            } else {
                trimmedChargeUnit = this.$_extractNumber(rate.chargeUnit);
                trimmedUnit = this.$_extractUnit(rate.chargeUnit);
            }

            // Initialize original edit buffer with current values
            originalEditBuffer.description = rate.description;
            originalEditBuffer.amount = this.$_getRateAmount(rate);
            originalEditBuffer.chargeUnit = trimmedChargeUnit;

            if (rate.type === rateType) {
                originalEditBuffer.timeInput = rate.chargeUnit;
            }
            
            this.originalChargeUnit = trimmedChargeUnit;
            this.trimmedUnit = trimmedUnit;
            this.showEditActions = false;
        },
        $_saveEdit(rateList, editBuffer, originalEditBuffer, entityType, entityId, chargeType, isManualCharge = false) {
            if (this.editingIndex !== null) {
                this.editingLoadingIndex = this.editingIndex;
                
                const rate = rateList[this.editingIndex];
                const rateType = 'hourly';

                let originalChargeUnit = originalEditBuffer.chargeUnit;
                if (rate.type === rateType) {
                    originalChargeUnit = this.$_convertToMinutes(originalEditBuffer.timeInput);
                }

                const { description: originalDescription, amount: originalAmount, timeInput: originalEditTime } = originalEditBuffer;
                const { description: editedDescription, amount: editedAmount, chargeUnit: editedChargeUnit, timeInput: editTime } = editBuffer;
                const originalUnit = this.originalChargeUnit;

                // This will be valid for jobs with multi charge facet.
                const currentBaseCharges = rateList.filter((x) => x.isBaseCharge).length;
                let otherBaseCharges = 0;
                if (currentBaseCharges > 1) {
                    otherBaseCharges = rateList.filter((x) => x.isBaseCharge === true && x.id !== rate.id);
                }

                const { rate: updatedRate, payload, useNewEndpoint } = this.$_prepareChargeRateEdit(
                    rate,
                    { description: originalDescription, amount: originalAmount, chargeUnit: originalChargeUnit, timeInput: originalEditTime },
                    { description: editedDescription, amount: editedAmount, chargeUnit: editedChargeUnit, timeInput: editTime },
                    originalUnit,
                    this.trimmedUnit,
                    isManualCharge,
                    otherBaseCharges,
                    chargeType
                );

                // Determine API endpoint based on entity type
                const api = this.$_getEditApiEndpoint(entityType, entityId, useNewEndpoint, currentBaseCharges, chargeType);
                rate.chargeUnit = updatedRate.chargeUnitDescription;

                return {
                    api,
                    payload,
                    entityType,
                    rate: updatedRate
                };
            }

            return null;
        },
        $_getEditApiEndpoint(entityType, entityId, useNewEndpoint, currentBaseCharges, chargeType) {
            // eslint-disable-next-line no-nested-ternary
            const baseEndpoint = useNewEndpoint ? 'unit' : '';
            
            if(currentBaseCharges > 1 && useNewEndpoint) {
                return `/api/rates/${entityId}/${entityType}/${chargeType}/multicharges`
            }

            const type = `${chargeType?.toLowerCase()}s`;

            switch (entityType.toLowerCase()) {
                case 'shipment':
                    return `/api/rates/${entityId}/Shipment/${baseEndpoint}${type}`;
                case 'stop':
                    return `/api/rates/${entityId}/Stop/${baseEndpoint}${type}`;
                case 'order':
                    return `/api/rates/${entityId}/Order/${baseEndpoint}${type}`;
                case 'run':
                    return `/api/rates/${entityId}/Run/${baseEndpoint}${type}`; 
                case 'trip':
                    return useNewEndpoint 
                        ? `/api/trips/${entityId}/unit${type}`
                        : `/api/trips/${entityId}/rate/${type}`;
                default:
                    return null;
            }
        },
        $_convertToMinutes(input) {
            const hoursMatch = input.match(/(\d+)h/);
            const minutesMatch = input.match(/(\d+)m/);

            const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
            const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;

            return (hours * 60) + minutes;
        },
        $_extractNumber(input) {
            const match = input.match(/[\d.]+/g);
            return match ? Number(match.join('')) : '';
        },
        $_extractUnit(input) {
            const match = input.match(/[a-zA-Z]+/g);
            return match ? match.join('').trim() : '';
        },
        $_validateHourlyTimeFormat(timeInput) {
            if (!timeInput || typeof timeInput !== 'string') {
                return false;
            }
            
            // Trim whitespace and convert to lowercase for case-insensitive matching
            const trimmedInput = timeInput.trim().toLowerCase();
            
            // Regex pattern to match: numbers followed by 'h' and/or numbers followed by 'm'
            // Examples: "04h 34m", "33h 59m", "40m", "40m", "5h", "0h 0m", "0h 1m"
            const timePattern = /^(\d+h\s*)?(\d+m\s*)?$/;
            
            // Check if the input matches the pattern
            if (!timePattern.test(trimmedInput)) {
                return false;
            }
            
            // Extract hours and minutes
            const hoursMatch = trimmedInput.match(/(\d+)h/);
            const minutesMatch = trimmedInput.match(/(\d+)m/);
            
            const hours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
            const minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
            
            // Validate that at least one of hours or minutes is present
            if (hours === 0 && minutes === 0) {
                return false;
            }
            
            // Allow any positive integer for hours (including > 24)
            if (hours < 0) {
                return false;
            }
            
            // Allow any positive integer for minutes (including > 59)
            if (minutes < 0 && minutes > 60) {
                return false;
            }
            
            return true;
        },
        $_ensureChargeField(fieldName) {
            const defaultField = { operator: '=', value: null };
            const currentField = this.rateDetail[fieldName];

            if (currentField === null || currentField === undefined) {
                this.$set(this.rateDetail, fieldName, { ...defaultField });
                return;
            }

            if (typeof currentField === 'number' || typeof currentField === 'string') {
                const parsedValue = currentField === '' ? null : Number(currentField);
                this.$set(this.rateDetail, fieldName, {
                    ...defaultField,
                    value: Number.isNaN(parsedValue) ? null : parsedValue
                });
                return;
            }

            if (typeof currentField === 'object') {
                if (!Object.prototype.hasOwnProperty.call(currentField, 'operator')) {
                    this.$set(currentField, 'operator', '=');
                }

                if (!Object.prototype.hasOwnProperty.call(currentField, 'value')) {
                    this.$set(currentField, 'value', null);
                }

                return;
            }

            this.$set(this.rateDetail, fieldName, { ...defaultField });
        },
        $_nullChecker(data) {
            if (data && data.value !== null && data.value !== '')
                return true;

            return false;
        }
    }
}
